import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useReactFlow, useViewport } from '@xyflow/react';
import useQueryParams from '../../hooks/useQueryParams';
import {
  selectCommentsByFunnel,
  selectFilterSortState,
  selectShowResolved,
} from '../../reducers/commentsReducer';
import type { MentionedUser } from '../common/comments/types';
import useEventListener from '../common/useEventListener';
import CommentsPanel from './CommentsPanel';
import NewCommentModal from './NewCommentModal';
import useComments from './useComments';
import { filterSortComments, getPositionRelativeToFunnel } from './helper';
import CommentPreviewAndThread from './CommentPreviewAndThread';
import { selectUserInfoData } from '../../reducers/userInfoReducer';
import { ACTIONS_EVENTS } from '../../services/events';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import useSweepNavigate from '../common/useSweepNavigate';
import { CommentThreadProps } from './types';

const cursorSizeCorrectionX = -15;
const cursorSizeCorrectionY = 8;

const CommentsLayer = ({
  funnelMapId,
  isCommentModeOn,
  closeCommentMode,
  reservedLeftSpace = 0,
}: {
  funnelMapId: string;
  isCommentModeOn: boolean;
  closeCommentMode: () => void;
  reservedLeftSpace?: number;
}) => {
  const canvasRef = window.document.getElementById('sweep-canvas');
  const { top: canvasTop, left: canvasLeft } = canvasRef?.getBoundingClientRect() ?? {
    top: 0,
    left: 0,
  };
  const { zoom, x, y } = useViewport();
  const currentUser = useSelector(selectUserInfoData);
  const commentThreads = useSelector(selectCommentsByFunnel(funnelMapId));
  const filterSortState = useSelector(selectFilterSortState);
  const showResolved = useSelector(selectShowResolved(funnelMapId));
  const commentsToShow = useMemo(() => {
    return commentThreads
      ? filterSortComments(commentThreads, filterSortState, showResolved, currentUser?.id ?? '')
      : [];
  }, [commentThreads, filterSortState, currentUser, showResolved]);
  const [addCommentEvent, setAddCommentEvent] =
    React.useState<React.MouseEvent<HTMLElement> | null>(null);
  const isNewCommentModalOpened = Boolean(addCommentEvent);
  const [commentsPanelOpened, setCommentsPanelOpened] = useState(true);
  const layerRef = useRef<HTMLDivElement>(null);
  const { createNewComment, fetchCommentThreads, updateThreadIsRead } = useComments();
  const query = useQueryParams();
  const rf = useReactFlow();
  const [commentThreadOpened, setCommentThreadOpened] = useState<string | null>();
  const sendBiEvent = useSendBiEvent();
  const { removeQueryParams } = useSweepNavigate();

  const openCommentThread = useCallback(
    (commentThread: CommentThreadProps) => {
      setCommentThreadOpened(commentThread.id);
      if (!currentUser?.id) return;
      if (!commentThread.isRead) {
        updateThreadIsRead({
          commentThreadId: commentThread.id,
          funnelMapId: commentThread.funnelMapId,
          isRead: true,
        });
      }
    },
    [currentUser?.id, updateThreadIsRead],
  );

  const closeCommentThread = () => {
    setCommentThreadOpened(null);
  };
  const commentId = query.get('comment');
  const comment = commentsToShow?.find(({ id }) => commentId === id);

  const setCommentInView = useCallback(
    (comment: CommentThreadProps) => {
      rf.setCenter(comment.positionX, comment.positionY, { zoom, duration: 300 });
      openCommentThread(comment);
    },
    [openCommentThread, rf, zoom],
  );

  useEffect(() => {
    if (!comment) {
      return;
    }
    setCommentInView(comment);
    removeQueryParams(['comment']);
  }, [comment, removeQueryParams, setCommentInView]);

  useEffect(() => {
    //no need to re-fetch upon re-enter the same funnel
    if (!commentThreads) {
      fetchCommentThreads(funnelMapId, true);
    }
  }, [fetchCommentThreads, funnelMapId, commentThreads]);

  useEffect(() => {
    if (isCommentModeOn) {
      setCommentsPanelOpened(true);
    }
  }, [isCommentModeOn]);

  const closeNewCommentModal = () => {
    setAddCommentEvent(null);
  };

  const catcherLayerClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    if (addCommentEvent) {
      closeNewCommentModal();
    } else {
      setAddCommentEvent(event);
    }
  };

  const addComment =
    ({ positionX, positionY }: { positionX: number; positionY: number }) =>
    async (commentBody: string, mentionedUserIds: MentionedUser[]) => {
      await createNewComment({
        commentBody,
        mentionedUserIds,
        funnelMapId,
        positionX,
        positionY,
      });
    };

  const onEscapeHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isNewCommentModalOpened) {
          closeNewCommentModal();
        } else {
          closeCommentMode();
        }
      }
    },
    [closeCommentMode, isNewCommentModalOpened],
  );

  const commentPanelCloseHandler = () => {
    setCommentsPanelOpened(false);
    closeCommentMode();
  };

  useEventListener('keydown', onEscapeHandler);

  return (
    <>
      {isCommentModeOn && (
        <div
          onClick={catcherLayerClickHandler}
          ref={layerRef}
          style={{
            cursor:
              "url('data:image/png;base64,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') 30 30, auto",
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            position: 'absolute',
            zIndex: 1,
          }}
        />
      )}

      {isCommentModeOn && commentsPanelOpened && (
        <CommentsPanel
          funnelMapId={funnelMapId}
          onClose={commentPanelCloseHandler}
          onCommentThreadClick={setCommentInView}
        />
      )}

      <div
        id="comments"
        style={{
          width: 0,
          height: 0,
          zIndex: 2,
          position: 'relative',
          transform: `translate(${x}px,${y}px) scale(${zoom})`,
          left: canvasLeft,
        }}
      >
        {commentsToShow.map((commentThread, idx) => (
          <CommentPreviewAndThread
            key={`comment-${idx}-${commentThread.id ?? ''}`}
            commentThread={commentThread}
            isCommentThreadOpened={commentThreadOpened === commentThread.id}
            funnelMapId={funnelMapId}
            openCommentThread={openCommentThread}
            closeCommentThread={closeCommentThread}
          />
        ))}
      </div>

      {isNewCommentModalOpened && addCommentEvent && layerRef?.current && (
        <NewCommentModal
          anchorEl={layerRef.current}
          handleClose={closeNewCommentModal}
          positionX={
            addCommentEvent.clientX + cursorSizeCorrectionX + reservedLeftSpace - canvasLeft
          }
          positionY={addCommentEvent.clientY + cursorSizeCorrectionY - canvasTop}
          onConfirm={(body, mentionedUserIds) => {
            sendBiEvent({ name: ACTIONS_EVENTS.addComment });
            addComment(
              getPositionRelativeToFunnel(
                addCommentEvent.clientX +
                  cursorSizeCorrectionX -
                  canvasLeft +
                  (reservedLeftSpace - canvasLeft),
                addCommentEvent.clientY + cursorSizeCorrectionY - canvasTop,
                x,
                y,
                zoom,
              ),
            )(body, mentionedUserIds);
          }}
        />
      )}
    </>
  );
};

export default CommentsLayer;
